import type { JSONSchema7 } from 'json-schema';
import { YamlValidationOptions } from 'Components/Editor';
import schemaBlueprint from 'Public/schemas/correlationrule_schema.json';
import { DETECTIONS_DOC_URL } from 'Source/constants';

const listOfSchemas = schemaBlueprint.definitions.MatchExpressions.items.oneOf;

// Exclude fields that are not used by the BE like `refs` and `definitions`.
// We can remove this once we update the `schema.json` file.

/**
 * @param {string[]} listOfGroupOrSequenceIds
 * List of suggestion ids for the ID field of each RuleOutput.
 * note: this corresponds to the ID of the sequence/group item, not the RuleID of the rule within that item.
 * @returns {YamlValidationOptions} for monaco-yaml
 */
export const setupCorrelationRuleTestCaseSchemaValidationOptions = (
  listOfGroupOrSequenceIds: string[],
): YamlValidationOptions => ({
  docUri: DETECTIONS_DOC_URL,
  schema: {
    type: 'object',
    properties: {
      ExpectedResult: {
        type: 'boolean',
        description: 'Should this test result in the correlation rule firing an alert?',
      },
      Name: {
        type: 'string',
        description: 'A user-friendly descriptor to give this test case',
      },
      RuleOutputs: {
        type: 'array',
        items: {
          properties: {
            ID: {
              anyOf: [
                {
                  type: 'string',
                },
                {
                  type: 'string',
                  enum: listOfGroupOrSequenceIds,
                },
              ],
              description: 'Must match the ID of an entry in the Sequence or Group section above',
            },
            Matches: {
              type: 'object',
            },
          },
        },
      },
    },
  } as JSONSchema7,
});

/**
 * @param {string[]} listOfRuleIds List of suggestion ids for the RuleID field
 * @returns {YamlValidationOptions} for monaco-yaml
 */
export const setUpCorrelationRuleSchemaValidationOptions = (
  listOfRuleIds: string[],
): YamlValidationOptions => ({
  docUri: DETECTIONS_DOC_URL,
  schema: {
    ...schemaBlueprint,
    definitions: {
      ...schemaBlueprint.definitions,
      MatchExpressions: {
        ...schemaBlueprint.definitions.MatchExpressions,
        items: {
          ...schemaBlueprint.definitions.MatchExpressions.items,
          oneOf: [
            {
              ...listOfSchemas[0],
              properties: {
                ...listOfSchemas[0].properties,
                Sequence: {
                  ...listOfSchemas[0].properties.Sequence,
                  items: {
                    ...listOfSchemas[0].properties.Sequence.items,
                    oneOf: listOfSchemas[0].properties.Sequence.items.oneOf.map(subSchema => ({
                      ...subSchema,
                      properties: {
                        ...subSchema.properties,
                        RuleID: { ...subSchema.properties.RuleID, enum: listOfRuleIds },
                      },
                    })),
                  },
                },
              },
            },
            {
              ...listOfSchemas[1],
              properties: {
                ...listOfSchemas[1].properties,
                Group: {
                  ...listOfSchemas[1].properties.Group,
                  items: {
                    ...listOfSchemas[1].properties.Group.items,
                    oneOf: listOfSchemas[1].properties.Group.items.oneOf.map(subSchema => ({
                      ...subSchema,
                      properties: {
                        ...subSchema.properties,
                        RuleID: { ...subSchema.properties.RuleID, enum: listOfRuleIds },
                      },
                    })),
                  },
                },
              },
            },
          ],
        },
      },
    },
  } as JSONSchema7,
});
